// filepath: apps/web/src/features/proc/RestoredOrphanBadge.tsx
//
// FR-UI-PROC-1 AC-4 — UX chrome for the literal `restored_orphan`
// reason code (FR-TEN-8, Phase 11).
//
// Three carriers per FR-UI-CORE-9 AC-2 (color is NOT the sole carrier):
//   1. An icon-like text marker (`!`) so screen-reader users hear
//      "Warning" before the label;
//   2. A translated label explaining the reason code;
//   3. A `data-state="restored_orphan"` attribute on the container
//      so tests + downstream consumers can key off the value.
//
// The component renders ONLY for `reason === RESTORED_ORPHAN_REASON`.
// Non-`restored_orphan` failures render the raw reason string
// verbatim via FailedInstanceRow (no translation).
//
// FR-UI-CORE-9: zero `dangerouslySetInnerHTML`. The label is rendered
// as a text node via `t()`.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { RESTORED_ORPHAN_REASON } from "./types";

export interface RestoredOrphanBadgeProps {
  /** The literal reason string from the API. */
  reason: string;
}

/**
 * RestoredOrphanBadge — explanatory badge for `restored_orphan`
 * failures.
 *
 * Returns `null` when the reason is not `restored_orphan`. This
 * keeps the call site readable:
 *
 *   <RestoredOrphanBadge reason={entry.failure_reason} />
 *
 * The parent (FailedInstanceRow) ALWAYS renders the raw reason
 * string verbatim via `{entry.failure_reason}` — the badge is
 * additive chrome, not a replacement.
 */
export function RestoredOrphanBadge({
  reason,
}: RestoredOrphanBadgeProps): React.ReactElement | null {
  if (reason !== RESTORED_ORPHAN_REASON) return null;
  const { t } = useTranslation();
  const label = t("failed_instance.restored_orphan_badge", "Restored orphan");
  const explainer = t(
    "failed_instance.restored_orphan_explainer",
    "This failure was recorded during tenant restore. The originating step may no longer be reproducible.",
  );

  return (
    <span
      data-testid="restored-orphan-badge"
      data-state="restored_orphan"
      className="restored-orphan-badge"
      role="note"
      aria-label={`${label}: ${explainer}`}
    >
      <span aria-hidden="true" className="restored-orphan-badge-marker">
        !
      </span>
      <span className="restored-orphan-badge-label">{label}</span>
      <span className="visually-hidden">: {explainer}</span>
    </span>
  );
}
